import React from 'react';
import { Car, Wrench, CreditCard, Key, CheckCircle, Calendar } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const iconMap = {
  'Fahrzeugverkauf': Car,
  'Werkstatt-Service': Wrench,
  'Finanzierung': CreditCard,
  'Probefahrt': Key,
};

// Bildoptimierung für Unsplash und Pexels
const optimizeImageUrl = (url) => {
  if (url.includes('unsplash.com')) {
    return `${url}?w=400&q=80&auto=format&fit=crop`;
  }
  if (url.includes('pexels.com')) {
    return `${url}?auto=compress&cs=tinysrgb&w=400`;
  }
  return url;
};

const imageMap = {
  'Fahrzeugverkauf': 'https://images.unsplash.com/photo-1574023278969-abb7ab49945c',
  'Werkstatt-Service': 'https://images.unsplash.com/photo-1730453075684-2ad6232ab451',
  'Finanzierung': 'https://images.unsplash.com/photo-1591527292000-95f01a0d1496',
  'Probefahrt': 'https://images.pexels.com/photos/28353829/pexels-photo-28353829.jpeg',
};

const werkstattServices = [
  'HU/AU (TÜV)',
  'Inspektion & Wartung',
  'Reifenservice',
  'Unfallinstandsetzung',
  'Klimaanlagen-Service',
  'Bremsenservice'
];

export const Services = ({ services }) => {
  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Unsere Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Vom Kauf bis zur Wartung – wir begleiten Sie auf jedem Schritt
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {services.map((service, index) => {
            const Icon = iconMap[service.title] || Car;
            const image = imageMap[service.title];
            return (
              <Card
                key={service.id}
                className="group overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border-0 rounded-2xl"
                style={{
                  animationDelay: `${index * 100}ms`,
                }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-gray-100">
                  <img
                    src={optimizeImageUrl(image)}
                    alt={service.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  
                  {/* Icon Overlay */}
                  <div className="absolute top-4 right-4 w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                    <Icon className="w-7 h-7 text-red-600" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>

                {/* Accent Border */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-red-600 transition-all duration-500 group-hover:h-2"></div>
              </Card>
            );
          })}
        </div>

        {/* Werkstatt-Services Detail Box */}
        <Card className="p-8 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border-0 shadow-2xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">Werkstatt-Services</h3>
              </div>
              <p className="text-gray-300 mb-6">
                Unsere zertifizierten Mechaniker kümmern sich um Ihr Fahrzeug – schnell, zuverlässig und zu fairen Preisen.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {werkstattServices.map((service, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-200">
                    <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
              </div>
              <a href="/werkstatt.html">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all duration-300 hover:scale-105">
                  <Calendar className="w-5 h-5" />
                  Werkstatt-Termin anfragen
                </Button>
              </a>
            </div>
            <div className="hidden md:block">
              <img
                src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=500&q=80&auto=format&fit=crop"
                alt="Werkstatt Service"
                className="rounded-xl shadow-lg"
              />
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};
